import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card,
    Descriptions,
    Table,
    Tag,
    Button,
    Space,
    Spin,
    message,
    Modal,
    Radio,
    Typography,
    Divider,
    Result,
    Tooltip,
    Popconfirm,
} from 'antd';
import {
    ArrowLeftOutlined,
    WalletOutlined,
    CreditCardOutlined,
    BankOutlined,
    PayCircleOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
    PrinterOutlined,
} from '@ant-design/icons';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements } from '@stripe/react-stripe-js';
import moment from 'moment';
import billingService from '../../services/billingService';
import paymentService from '../../services/paymentService';
import StripePaymentElementForm, { PaymentErrorAlert } from '../../components/StripePaymentElementForm';
import StripePendingPayment from '../../components/StripePendingPayment';
import { stripeErrorMessage } from '../../utils/stripeErrors';
import { printInvoice } from './InvoicePrint';

import { userPath } from '../../../shared/config/paths';
import { formatMoney, getDisplayCurrency, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import { reportPurchase } from '../../utils/adsConversion';

// 账单是记录：金额一律按账单自己的币种显示（不按偏好货币折算）；表头另附一个折算参考值
const money = (amount, currency, opts = {}) => formatMoney(amount, currency, { ...opts, original: true });

const { Title, Text } = Typography;

const radioBox = { padding: '12px', border: '1px solid #d9d9d9', borderRadius: '8px', width: '100%' };

const InvoiceDetail = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('balance');
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [savedCards, setSavedCards] = useState([]);
    const [selectedSavedCard, setSelectedSavedCard] = useState(null);
    const [balance, setBalance] = useState(0);
    const [balanceCurrency, setBalanceCurrency] = useState(PLATFORM_CURRENCY);
    const [stripeConfig, setStripeConfig] = useState(null);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    // Stripe Payment Element: the PaymentIntent being paid in the modal
    const [intent, setIntent] = useState(null);
    // In-flight Stripe payment (ACH processing / bank transfer instructions); the bill stays unpaid until the webhook confirms
    const [pending, setPending] = useState(null);
    // Stripe confirmed the charge; waiting for our webhook to mark the bill paid
    const [settling, setSettling] = useState(false);
    const [payError, setPayError] = useState(null);
    const [replacing, setReplacing] = useState(false);
    const settleTimer = useRef(null);

    // Initialize Stripe
    const stripePromise = useMemo(() => {
        if (stripeConfig?.publishable_key) {
            return loadStripe(stripeConfig.publishable_key);
        }
        return null;
    }, [stripeConfig?.publishable_key]);

    // Custom payment methods shown inside the Payment Element (explicit allow-list from the API; PayPal → our PayPal flow)
    const customMethods = stripeConfig?.custom_payment_methods || [];
    const paypalInElement = customMethods.some((m) => m.handler === 'paypal');

    const loadPending = useCallback(async () => {
        try {
            const r = await paymentService.getStripePending(id);
            setPending(r?.success ? (r.data?.pending || null) : null);
        } catch (e) {
            console.warn('Failed to load pending payment', e);
        }
    }, [id]);

    // Load invoice details
    const loadInvoice = useCallback(async () => {
        setLoading(true);
        try {
            const response = await billingService.getInvoice(id);
            if (response.success) {
                setInvoice(response.data);

                // 充值账单不能用余额支付，默认选卡
                if (response.data.invoice_type === 'topup') {
                    setSelectedPaymentMethod((prev) => (prev === 'balance' ? 'card' : prev));
                }

                // Check URL params for payment result
                const paymentParam = searchParams.get('payment') || searchParams.get('paypal');
                if (paymentParam === 'success' && response.data.status === 'paid') {
                    setPaymentSuccess(true);
                }
                if (response.data.status === 'unpaid' && response.data.payment_pending) {
                    loadPending();
                } else {
                    setPending(null);
                }
            } else {
                message.error(response.error || t('Failed to load invoices'));
            }
        } catch (error) {
            if (!error?.toasted) message.error(error?.error || error?.message || t('Failed to load invoices'));
        } finally {
            setLoading(false);
        }
    }, [id, searchParams, i18n.language, loadPending]); // 明细/备注按语言由 API 渲染：切换语言重新拉

    // Load payment methods
    const loadPaymentMethods = async () => {
        try {
            const response = await paymentService.getPaymentMethods();
            if (response.success) {
                setPaymentMethods(response.data.methods || []);
                setSavedCards(response.data.saved_methods || []);
                setBalance(parseFloat(response.data.balance) || 0);
                if (response.data.balance_currency) setBalanceCurrency(response.data.balance_currency);
            }
        } catch (error) {
            console.error('Failed to load payment methods:', error);
        }
    };

    // Options the server reports as available (unavailable ones are hidden, never offered then failed)
    const hasMethod = (id) => paymentMethods.some((m) => m.id === id);

    // Load Stripe config
    const loadStripeConfig = async () => {
        try {
            const response = await paymentService.getStripeConfig();
            if (response.success) {
                setStripeConfig(response.data);
            }
        } catch (error) {
            console.error('Failed to load Stripe config:', error);
        }
    };

    useEffect(() => {
        loadInvoice();
        loadPaymentMethods();
        loadStripeConfig();
    }, [loadInvoice]);

    useEffect(() => () => clearTimeout(settleTimer.current), []);

    // 付款成功 → Google Ads 购买转化(每张账单一次;充值不算)
    useEffect(() => {
        if (paymentSuccess && invoice) reportPurchase(invoice);
    }, [paymentSuccess, invoice]);

    // Stripe charged; the bill is marked paid by our webhook (usually within seconds). Poll our own API, not Stripe.
    const startSettling = useCallback(() => {
        setSettling(true);
        clearTimeout(settleTimer.current);
        let tries = 0;
        const tick = async () => {
            tries += 1;
            try {
                const r = await billingService.getInvoice(id);
                if (r?.success && r.data?.status === 'paid') {
                    setInvoice(r.data);
                    setSettling(false);
                    setPending(null);
                    setPaymentSuccess(true);
                    return;
                }
            } catch (e) {
                // keep polling
            }
            if (tries < 20) settleTimer.current = setTimeout(tick, 3000);
        };
        settleTimer.current = setTimeout(tick, 1500);
    }, [id]);

    // Back from a redirect-based method (Klarna, Cash App Pay, Amazon Pay, 3DS…): show the state, never settle here
    useEffect(() => {
        const pi = searchParams.get('payment_intent');
        if (!pi) return;
        (async () => {
            try {
                const r = await paymentService.checkStripeStatus(pi);
                const st = r?.data?.status;
                if (r?.data?.invoice_paid) {
                    setPaymentSuccess(true);
                } else if (st === 'succeeded') {
                    startSettling();
                } else if (st === 'processing' || st === 'requires_action') {
                    loadPending();
                } else if (st === 'requires_payment_method') {
                    setPayError(stripeErrorMessage({ error_code: r?.data?.error_code }, t));
                }
            } catch (e) {
                console.warn('Payment status check failed', e);
            } finally {
                const next = new URLSearchParams(searchParams);
                ['payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach((k) => next.delete(k));
                setSearchParams(next, { replace: true });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeModal = () => {
        setPaymentModalVisible(false);
        setIntent(null);
        setPayError(null);
    };

    // Stripe result from the Payment Element or a saved method
    const handleStripeOutcome = (status) => {
        closeModal();
        if (status === 'succeeded') {
            startSettling();
        } else {
            // processing (ACH) / requires_action (bank transfer instructions, microdeposits)
            loadInvoice();
            loadPending();
        }
    };

    const startPayPal = async () => {
        const response = await paymentService.createPayPalOrder(invoice.id);
        if (response.success) {
            window.location.href = response.data.approve_url;
        } else {
            setPayError(stripeErrorMessage(response, t));
        }
    };

    // Open the Payment Element (or report the pending bank transfer / processing payment instead)
    const openPaymentElement = async (replacePending = false) => {
        try {
            const response = await paymentService.createStripeIntent(invoice.id, { replacePending });
            if (response.success) {
                setIntent(response.data);
                setPending(null);
                setPaymentModalVisible(true);
            } else {
                setPayError(stripeErrorMessage(response, t));
                setPaymentModalVisible(true);
            }
        } catch (err) {
            if (err?.data?.pending) {
                // PENDING_BANK_TRANSFER / PAYMENT_PROCESSING: show it on the page, not as an error
                closeModal();
                setPending(err.data.pending);
                return;
            }
            console.warn('create payment failed', err);
            setPayError(stripeErrorMessage(err, t));
            setPaymentModalVisible(true);
        }
    };

    const handlePayAnotherWay = async () => {
        setReplacing(true);
        setSelectedPaymentMethod('card');
        try {
            await openPaymentElement(true);
        } finally {
            setReplacing(false);
        }
    };

    // 余额应扣额（余额币种）：同币种 = 发票总额；跨币种 = 后端 balance_equivalent；拿不到汇率为 null（交给后端判定）
    const sameCurrency = invoice ? String(invoice.currency || balanceCurrency).toUpperCase() === String(balanceCurrency).toUpperCase() : null;
    const balanceDue = !invoice ? null
        : sameCurrency ? parseFloat(invoice.total_amount || 0)
        : (invoice.balance_equivalent?.amount != null ? parseFloat(invoice.balance_equivalent.amount) : null);

    // Handle payment
    const handlePayment = async () => {
        if (!selectedPaymentMethod) {
            setPayError({ text: t('Choose a payment method'), detail: null });
            return;
        }

        setPaying(true);
        setPayError(null);

        try {
            if (selectedPaymentMethod === 'balance') {
                // 余额币种与发票币种可能不同：用后端按汇率折算的应扣额比较（最终以后端为准）
                const balanceNum = parseFloat(balance || 0);
                if (balanceDue !== null && balanceNum < balanceDue) {
                    setPayError({ text: t('Insufficient balance: need {{need}}, available {{have}}', { need: money(balanceDue, balanceCurrency), have: money(balanceNum, balanceCurrency) }), detail: null });
                    return;
                }
                const response = await billingService.payInvoice(invoice.id, 'balance');
                if (response.success) {
                    message.success(t('Payment successful'));
                    closeModal();
                    setPaymentSuccess(true);
                    loadInvoice();
                } else {
                    setPayError(stripeErrorMessage(response, t));
                }
            } else if (selectedPaymentMethod === 'saved_card' && selectedSavedCard) {
                const response = await paymentService.chargeWithSavedCard(invoice.id, selectedSavedCard);
                const st = response?.data?.status;
                if (response.success && (st === 'succeeded' || st === 'processing')) {
                    handleStripeOutcome(st);
                } else if (response.success && st === 'requires_action') {
                    // 3D Secure etc. for a saved card
                    const stripe = await stripePromise;
                    const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret: response.data.client_secret });
                    if (error) {
                        console.warn('handleNextAction error', error);
                        setPayError(stripeErrorMessage(error, t));
                    } else {
                        handleStripeOutcome(paymentIntent?.status === 'succeeded' ? 'succeeded' : paymentIntent?.status);
                    }
                } else {
                    setPayError(stripeErrorMessage(response, t));
                }
            } else if (selectedPaymentMethod === 'card') {
                await openPaymentElement(false);
            } else if (selectedPaymentMethod === 'paypal') {
                await startPayPal();
            }
        } catch (error) {
            // API error bodies ({error, error_code}) or Stripe.js errors → one short message, raw text in the tooltip
            console.warn('Payment failed', error);
            // balance / PayPal requests already got the global error toast from apiClient; Stripe calls skip it
            const globalToasted = ['balance', 'paypal'].includes(selectedPaymentMethod) && !error?.type;
            if (!globalToasted) setPayError(stripeErrorMessage(error, t));
        } finally {
            setPaying(false);
        }
    };

    const getStatusTag = (status) => {
        const statusMap = {
            draft: { color: 'default', text: 'Draft' },
            unpaid: { color: 'warning', text: 'Unpaid' },
            paid: { color: 'success', text: 'Paid' },
            cancelled: { color: 'default', text: 'Cancelled' },
            refunded: { color: 'processing', text: 'Refunded' },
            void: { color: 'error', text: 'Void' },
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    // Invoice items columns
    const itemColumns = [
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: t('Quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
            width: 100,
            align: 'center',
        },
        {
            title: t('Unit price'),
            dataIndex: 'unit_price',
            key: 'unit_price',
            width: 120,
            align: 'right',
            render: (price) => `${invoice?.currency || 'USD'} ${price}`,
        },
        {
            title: t('Amount'),
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (amount) => `${invoice?.currency || 'USD'} ${amount}`,
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!invoice) {
        return (
            <Result
                status="404"
                title={t('Invoice not found')}
                extra={
                    <Button type="primary" onClick={() => navigate(userPath('/billing/invoices'))}>
                        {t('Back to invoices')}
                    </Button>
                }
            />
        );
    }

    if (settling && invoice.status !== 'paid') {
        return (
            <Result
                icon={<LoadingOutlined />}
                title={t('Payment received')}
                subTitle={t('Finalizing your bill. This usually takes a few seconds.')}
                extra={<Button onClick={() => { setSettling(false); loadInvoice(); }}>{t('View invoice details')}</Button>}
            />
        );
    }

    if (paymentSuccess) {
        return (
            <Result
                status="success"
                icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                title={t('Payment successful')}
                subTitle={`Invoice ${invoice.invoice_number} has been paid successfully.`}
                extra={[
                    <Button type="primary" key="invoices" onClick={() => navigate(userPath('/billing/invoices'))}>
                        {t('View all invoices')}
                    </Button>,
                    <Button key="continue" onClick={() => setPaymentSuccess(false)}>
                        {t('View invoice details')}
                    </Button>,
                ]}
            />
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(userPath('/billing/invoices'))}>
                    {t('Back to invoices')}
                </Button>
                <Button icon={<PrinterOutlined />} onClick={() => printInvoice(invoice)}>
                    {t('Print / Export PDF')}
                </Button>
            </div>

            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Invoice {invoice.invoice_number}
                        </Title>
                        <Text type="secondary">{invoice.description}</Text>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        {getStatusTag(invoice.status)}
                        {invoice.status === 'unpaid' && (pending?.state || invoice.payment_pending) && (
                            <Tag color="processing">{['awaiting_transfer'].includes(pending?.state || invoice.payment_pending) ? t('Awaiting transfer') : t('Processing')}</Tag>
                        )}
                        <div style={{ marginTop: 8 }}>
                            <Title level={2} style={{ margin: 0, color: invoice.status === 'unpaid' ? '#ff4d4f' : '#52c41a' }}>
                                {money(invoice.total_amount, invoice.currency || PLATFORM_CURRENCY)}
                            </Title>
                            {getDisplayCurrency() && getDisplayCurrency() !== String(invoice.currency || PLATFORM_CURRENCY).toUpperCase() && (
                                <Text type="secondary">≈ {formatMoney(invoice.total_amount, invoice.currency || PLATFORM_CURRENCY)}</Text>
                            )}
                        </div>
                    </div>
                </div>

                <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label={t('Invoice type')}>{t({
                        subscription: 'New order', renewal: 'Renewal', upgrade: 'Upgrade', addon: 'Add-on pack', usage: 'Overage usage',
                        topup: 'Top up', ssl: 'SSL certificate', domain_registration: 'Domain registration', domain_renewal: 'Domain renewal',
                        domain_transfer: 'Domain transfer', adjustment: 'Adjustment',
                    }[invoice.invoice_type] || invoice.invoice_type)}</Descriptions.Item>
                    <Descriptions.Item label={t('Status')}>{getStatusTag(invoice.status)}</Descriptions.Item>
                    <Descriptions.Item label={t('Issue date')}>{moment(invoice.issue_date).format('YYYY-MM-DD')}</Descriptions.Item>
                    <Descriptions.Item label={t('Due date')}>{moment(invoice.due_date).format('YYYY-MM-DD')}</Descriptions.Item>
                    <Descriptions.Item label={t('Created at')}>{moment(invoice.created_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
                    {invoice.paid_at && (
                        <Descriptions.Item label={t('Paid at')}>{moment(invoice.paid_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
                    )}
                    {invoice.payment_method && (
                        <Descriptions.Item label={t('Payment method')}>{invoice.payment_method}</Descriptions.Item>
                    )}
                </Descriptions>

                <Divider />

                <Title level={5}>{t('Invoice items')}</Title>
                <Table
                    columns={itemColumns}
                    dataSource={invoice.items || []}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    summary={() => (
                        <Table.Summary>
                            <Table.Summary.Row>
                                <Table.Summary.Cell colSpan={3} align="right">
                                    <Text strong>{t('Subtotal:')}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell align="right">
                                    <Text>{money(invoice.subtotal, invoice.currency || PLATFORM_CURRENCY)}</Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                            {invoice.discount_amount > 0 && (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell colSpan={3} align="right">
                                        <Text type="success">{t('Discount:')}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align="right">
                                        <Text type="success">{money(invoice.discount_amount, invoice.currency || PLATFORM_CURRENCY, { negative: true })}</Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                            {invoice.tax_amount > 0 && (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell colSpan={3} align="right">
                                        <Text>Tax ({invoice.tax_rate}%):</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align="right">
                                        <Text>{money(invoice.tax_amount, invoice.currency || PLATFORM_CURRENCY)}</Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                            <Table.Summary.Row>
                                <Table.Summary.Cell colSpan={3} align="right">
                                    <Title level={5} style={{ margin: 0 }}>{t('Total:')}</Title>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell align="right">
                                    <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                                        {money(invoice.total_amount, invoice.currency || PLATFORM_CURRENCY)}
                                    </Title>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />

                {invoice.status === 'unpaid' && pending && (
                    <div style={{ marginTop: 24 }}>
                        <StripePendingPayment pending={pending} onPayAnotherWay={handlePayAnotherWay} replacing={replacing} />
                    </div>
                )}

                {invoice.status === 'unpaid' && !['processing', 'partially_funded'].includes(pending?.state) && (
                    <div style={{ marginTop: 24, textAlign: 'center' }}>
                        <Space size="middle">
                            <Button
                                type="primary"
                                size="large"
                                icon={<WalletOutlined />}
                                onClick={() => {
                                    setPayError(null);
                                    if (selectedPaymentMethod === 'card' && hasMethod('card')) openPaymentElement(false);
                                    else setPaymentModalVisible(true);
                                }}
                            >
                                {t('Pay now')}
                            </Button>
                            {invoice.invoice_type !== 'usage' && !(Number(invoice.paid_amount) > 0) && !pending && (
                                <Popconfirm title={t('Cancel this invoice?')} okText={t('Cancel invoice')} cancelText={t('Back')}
                                    onConfirm={async () => {
                                        try {
                                            const r = await billingService.cancelInvoice(invoice.id);
                                            if (r?.success) {
                                                message.success(t('Invoice cancelled'));
                                                loadInvoice();
                                            } else {
                                                message.error(r?.error || t('Cancellation failed'));
                                            }
                                        } catch (e) {
                                            message.error(e?.error || t('Cancellation failed'));
                                        }
                                    }}>
                                    <Button size="large">{t('Cancel invoice')}</Button>
                                </Popconfirm>
                            )}
                        </Space>
                    </div>
                )}

                {invoice.notes && (
                    <>
                        <Divider />
                        <Title level={5}>{t('Notes')}</Title>
                        <Text>{invoice.notes}</Text>
                    </>
                )}
            </Card>

            {/* Payment Modal */}
            <Modal
                title={intent ? t('Pay {{amount}}', { amount: money(intent.amount ?? invoice.total_amount, intent.currency || invoice.currency || PLATFORM_CURRENCY) }) : t('Choose a payment method')}
                open={paymentModalVisible}
                onCancel={closeModal}
                destroyOnClose
                footer={intent ? null : [
                    <Button key="cancel" onClick={closeModal}>
                        {t('Cancel')}
                    </Button>,
                    <Button
                        key="pay"
                        type="primary"
                        loading={paying}
                        onClick={handlePayment}
                        disabled={selectedPaymentMethod === 'saved_card' && !selectedSavedCard}
                    >
                        {selectedPaymentMethod === 'card' ? t('Continue') : t('Pay {{amount}}', { amount: money(invoice.total_amount, invoice.currency || PLATFORM_CURRENCY) })}
                    </Button>,
                ]}
                width={560}
            >
                {intent && stripePromise ? (
                    <div>
                        <Button type="link" onClick={() => { setIntent(null); setPayError(null); }} style={{ padding: 0, marginBottom: 12 }}>
                            ← {t('Back to payment methods')}
                        </Button>
                        <Elements
                            key={intent.client_secret}
                            stripe={stripePromise}
                            options={{
                                clientSecret: intent.client_secret,
                                // Stripe's own "save for future payments" checkbox; saved methods are used for auto-renew / LLM auto top-up
                                ...(intent.customer_session_client_secret ? { customerSessionClientSecret: intent.customer_session_client_secret } : {}),
                                appearance: { theme: 'stripe' },
                                ...(customMethods.length ? { customPaymentMethods: customMethods.map((m) => ({ id: m.id, options: { type: 'static' } })) } : {}),
                            }}
                        >
                            <StripePaymentElementForm
                                amount={intent.amount ?? invoice.total_amount}
                                currency={intent.currency || invoice.currency || PLATFORM_CURRENCY}
                                settleAmount={intent.invoice_amount ?? invoice.total_amount}
                                settleCurrency={intent.invoice_currency || invoice.currency || PLATFORM_CURRENCY}
                                returnUrl={`${window.location.origin}${userPath(`/billing/invoices/${invoice.id}`)}`}
                                customMethods={customMethods}
                                onDone={handleStripeOutcome}
                                onCustomMethod={async (handler) => { if (handler === 'paypal') await startPayPal(); }}
                            />
                        </Elements>
                    </div>
                ) : (
                    <Radio.Group
                        value={selectedPaymentMethod}
                        onChange={(e) => {
                            setSelectedPaymentMethod(e.target.value);
                            setSelectedSavedCard(null);
                            setPayError(null);
                            // straight to the Payment Element (Apple Pay / Google Pay one-tap buttons on top)
                            if (e.target.value === 'card') openPaymentElement(false);
                        }}
                        style={{ width: '100%' }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {/* Balance — 充值账单不能用余额支付（循环） */}
                            {invoice.invoice_type !== 'topup' && (
                                <Radio value="balance" style={radioBox}>
                                    <Space>
                                        <WalletOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{t('Balance')}</div>
                                            <Text type="secondary">{t('Available: {{v}}', { v: money(balance, balanceCurrency) })}</Text>
                                            {balanceDue !== null && sameCurrency === false && (
                                                <Tooltip title={invoice.balance_equivalent?.rate ? `1 ${invoice.currency} = ${invoice.balance_equivalent.rate} ${balanceCurrency}` : ''}>
                                                    <Text type="secondary" style={{ marginLeft: 8 }}>≈ {money(balanceDue, balanceCurrency)}</Text>
                                                </Tooltip>
                                            )}
                                            {balanceDue !== null && parseFloat(balance || 0) < balanceDue && (
                                                <>
                                                    <Tag color="red" style={{ marginLeft: 8 }}>{t('Insufficient balance')}</Tag>
                                                    <Button type="link" size="small" style={{ padding: 0 }}
                                                        onClick={(e) => { e.preventDefault(); navigate(userPath('/billing/balance')); }}>
                                                        {t('Top up')}
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </Space>
                                </Radio>
                            )}

                            {/* Saved cards / bank accounts */}
                            {savedCards.length > 0 && hasMethod('saved_card') && (
                                <Radio value="saved_card" style={radioBox}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Space>
                                            <CreditCardOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                                            <div style={{ fontWeight: 500 }}>{t('Saved payment method')}</div>
                                        </Space>
                                        {selectedPaymentMethod === 'saved_card' && (
                                            <Radio.Group value={selectedSavedCard} onChange={(e) => setSelectedSavedCard(e.target.value)} style={{ marginLeft: 28 }}>
                                                <Space direction="vertical">
                                                    {savedCards.map((m) => (
                                                        <Radio key={m.id} value={m.id}>
                                                            {m.type === 'us_bank_account' ? <BankOutlined style={{ marginRight: 6 }} /> : null}
                                                            {m.label || m.display_name || `${(m.card_brand || '').toUpperCase()} •••• ${m.card_last4 || m.last4 || ''}`}
                                                            {m.currency && <Tag style={{ marginLeft: 8 }}>{m.currency}</Tag>}
                                                            {m.is_default && <Tag color="blue" style={{ marginLeft: 8 }}>{t('Default')}</Tag>}
                                                        </Radio>
                                                    ))}
                                                </Space>
                                            </Radio.Group>
                                        )}
                                    </Space>
                                </Radio>
                            )}

                            {/* Stripe Payment Element: card, Apple Pay, Google Pay, Link, bank account, bank transfer… */}
                            {hasMethod('card') && (
                                <Radio value="card" style={radioBox}>
                                    <Space>
                                        <CreditCardOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{t('Card, wallet or bank')}</div>
                                            <Text type="secondary">{t('Card, Alipay, Apple Pay, Google Pay, bank account and more')}</Text>
                                        </div>
                                    </Space>
                                </Radio>
                            )}

                            {/* PayPal (own integration; hidden when it is offered inside the Payment Element) */}
                            {hasMethod('paypal') && !paypalInElement && (
                                <Radio value="paypal" style={radioBox}>
                                    <Space>
                                        <PayCircleOutlined style={{ fontSize: 20, color: '#003087' }} />
                                        <div>
                                            <div style={{ fontWeight: 500 }}>PayPal</div>
                                            <Text type="secondary">{t('Pay with your PayPal account')}</Text>
                                        </div>
                                    </Space>
                                </Radio>
                            )}
                        </Space>
                    </Radio.Group>
                )}
                {!intent && <PaymentErrorAlert error={payError} style={{ marginTop: 16 }} />}
            </Modal>
        </div>
    );
};

export default InvoiceDetail;
